// Cloudinary configuration and utilities
export interface CloudinaryConfig {
  cloudName: string;
  apiKey: string;
  apiSecret: string;
}

export interface UploadResult {
  publicId: string;
  url: string;
  secureUrl: string;
  width: number;
  height: number;
  format: string;
  resourceType: string;
}

class CloudinaryService {
  private config: CloudinaryConfig | null = null;
  private uploadPreset: string = 'ml_default';

  constructor() {
    // Cloudinary config will be set via environment variables
    const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
    const apiKey = import.meta.env.VITE_CLOUDINARY_API_KEY;
    
    if (cloudName && apiKey) {
      this.config = {
        cloudName,
        apiKey,
        apiSecret: import.meta.env.VITE_CLOUDINARY_API_SECRET || '',
      };
      
      // Use custom upload preset if provided, otherwise try common presets
      const customPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;
      if (customPreset) {
        this.uploadPreset = customPreset;
      } else {
        // Try to find a working upload preset
        this.uploadPreset = 'ml_default';
        console.warn('No upload preset specified. Using default "ml_default". If uploads fail, please create a custom upload preset in your Cloudinary dashboard.');
      }
    }
  }

  isConfigured(): boolean {
    return this.config !== null;
  }

  // Add method to validate configuration
  async validateConfiguration(): Promise<{ isValid: boolean; error?: string }> {
    if (!this.config) {
      return { isValid: false, error: 'Cloudinary is not configured' };
    }

    // Check if we have the required configuration
    if (!this.config.cloudName || !this.config.apiKey) {
      return { isValid: false, error: 'Cloudinary credentials are missing' };
    }

    // If config exists with required fields, consider it valid
    // Test uploads will fail gracefully during actual upload attempts
    return { isValid: true };
  }

  // Helper method to get upload preset creation instructions
  getUploadPresetInstructions(): string {
    return `
To fix Cloudinary upload issues, you need to create an upload preset:

1. Go to your Cloudinary Dashboard: https://cloudinary.com/console
2. Navigate to Settings > Upload
3. Scroll down to "Upload presets"
4. Click "Add upload preset"
5. Configure the preset:
   - Name: Choose a name (e.g., "bantay-sp-uploads")
   - Signing Mode: "Unsigned" (for client-side uploads)
   - Folder: "bantay-sp" (optional)
   - Allowed formats: "jpg, png, gif, webp"
   - Max file size: 10MB
6. Save the preset
7. Update your .env file:
   VITE_CLOUDINARY_UPLOAD_PRESET=your_preset_name

Current upload preset: ${this.uploadPreset}
    `.trim();
  }

  // Method to try alternative upload presets
  private async tryAlternativePresets(file: File, folder: string): Promise<UploadResult> {
    const alternativePresets = ['bantay-sp-uploads', 'bantay-sp', 'ml_default', 'general'];
    
    for (const preset of alternativePresets) {
      if (preset === this.uploadPreset) continue; // Skip current preset
      
      try {
        console.log(`Trying alternative upload preset: ${preset}`);
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', preset);
        formData.append('folder', folder);

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${this.config!.cloudName}/image/upload`,
          {
            method: 'POST',
            body: formData,
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log(`✅ Alternative preset "${preset}" worked!`);
          
          // Update the current preset to the working one
          this.uploadPreset = preset;
          
          return {
            publicId: result.public_id,
            url: result.url,
            secureUrl: result.secure_url,
            width: result.width,
            height: result.height,
            format: result.format,
            resourceType: result.resource_type,
          };
        }
      } catch (error) {
        console.log(`❌ Alternative preset "${preset}" failed:`, error);
        continue;
      }
    }
    
    throw new Error('All upload presets failed. Please create a custom upload preset in your Cloudinary dashboard.');
  }

  async uploadImage(file: File, folder: string = 'bantay-sp'): Promise<UploadResult> {
    if (!this.config) {
      throw new Error('Cloudinary is not configured');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', this.uploadPreset);
    formData.append('folder', folder);

    // Log upload attempt for debugging
    console.log('Attempting Cloudinary upload:', {
      cloudName: this.config.cloudName,
      uploadPreset: this.uploadPreset,
      folder,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${this.config.cloudName}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        // Get detailed error information
        let errorDetails = '';
        try {
          const errorResponse = await response.json();
          errorDetails = errorResponse.error?.message || errorResponse.error || JSON.stringify(errorResponse);
        } catch (parseError) {
          errorDetails = response.statusText;
        }
        
        console.error('Cloudinary upload failed:', {
          status: response.status,
          statusText: response.statusText,
          errorDetails,
          cloudName: this.config.cloudName,
          uploadPreset: this.uploadPreset
        });
        
        // If it's a 400 error, try alternative presets
        if (response.status === 400) {
          console.log('🔄 Trying alternative upload presets...');
          try {
            return await this.tryAlternativePresets(file, folder);
          } catch (fallbackError) {
            console.error('All alternative presets failed:', fallbackError);
            throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorDetails}. ${fallbackError instanceof Error ? fallbackError.message : ''}`);
          }
        }
        
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorDetails}`);
      }

      const result = await response.json();
      
      console.log('Cloudinary upload successful:', {
        publicId: result.public_id,
        url: result.url,
        secureUrl: result.secure_url
      });
      
      return {
        publicId: result.public_id,
        url: result.url,
        secureUrl: result.secure_url,
        width: result.width,
        height: result.height,
        format: result.format,
        resourceType: result.resource_type,
      };
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      throw new Error(`Failed to upload image to Cloudinary: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async uploadVideo(file: File, folder: string = 'bantay-sp/videos'): Promise<UploadResult> {
    if (!this.config) {
      throw new Error('Cloudinary is not configured');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', this.uploadPreset);
    formData.append('folder', folder);
    formData.append('resource_type', 'video');

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${this.config.cloudName}/video/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      return {
        publicId: result.public_id,
        url: result.url,
        secureUrl: result.secure_url,
        width: result.width,
        height: result.height,
        format: result.format,
        resourceType: result.resource_type,
      };
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      throw new Error('Failed to upload video to Cloudinary');
    }
  }

  getOptimizedUrl(publicId: string, options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: 'auto' | 'webp' | 'jpg' | 'png';
  } = {}): string {
    if (!this.config) {
      throw new Error('Cloudinary is not configured');
    }

    const { width, height, quality = 80, format = 'auto' } = options;
    let url = `https://res.cloudinary.com/${this.config.cloudName}/image/upload`;

    // Add transformations
    const transformations = [];
    if (width) transformations.push(`w_${width}`);
    if (height) transformations.push(`h_${height}`);
    transformations.push(`q_${quality}`);
    transformations.push(`f_${format}`);

    if (transformations.length > 0) {
      url += `/${transformations.join(',')}`;
    }

    return `${url}/${publicId}`;
  }

  async deleteResource(publicId: string, resourceType: 'image' | 'video' = 'image'): Promise<void> {
    if (!this.config) {
      throw new Error('Cloudinary is not configured');
    }

    try {
      // For client-side deletion, we need to use a different approach
      // Since we can't generate proper signatures on the client side,
      // we'll use a more robust error handling approach
      
      console.log(`Attempting to delete resource: ${publicId} (${resourceType})`);
      
      // Note: Client-side deletion of Cloudinary resources requires proper authentication
      // This is a limitation of Cloudinary's security model
      // In production, you should implement this via a serverless function or API route
      
      throw new Error(
        'Client-side deletion of Cloudinary resources is not supported for security reasons. ' +
        'Please implement deletion via a serverless function or API route. ' +
        'Resource ID: ' + publicId
      );
    } catch (error) {
      console.error('Cloudinary delete error:', error);
      throw new Error('Failed to delete resource from Cloudinary');
    }
  }
}

// Export singleton instance
export const cloudinary = new CloudinaryService(); 